import { type FC, useState } from "react";
import {
	getDefaultMetaAdsOptimizationGoal,
	MetaAdsAttributionWindow,
	MetaAdsBudgetType,
	MetaAdsCampaignObjective,
	MetaAdsConversionEvent,
	MetaAdsConversionLocation,
	MetaAdsOptimizationGoal,
} from "shared";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { useCampaignWizard } from "./CampaignWizardContext";
import { centsToDollars, dollarsToCents } from "./metaAdsMoney";

const OBJECTIVE_DESCRIPTIONS: Record<MetaAdsCampaignObjective, string> = {
	[MetaAdsCampaignObjective.OUTCOME_AWARENESS]:
		"Maximize reach and brand recall among your target audience.",
	[MetaAdsCampaignObjective.OUTCOME_TRAFFIC]:
		"Drive people to your website or landing page.",
	[MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT]:
		"Get more post engagement, page likes, or messaging conversations.",
	[MetaAdsCampaignObjective.OUTCOME_LEADS]:
		"Collect leads through instant forms, website, or messaging.",
	[MetaAdsCampaignObjective.OUTCOME_SALES]:
		"Drive purchases and conversions on your website.",
};

export const StepCampaign: FC = () => {
	const { draft, updateDraft, nextStep } = useCampaignWizard();
	const [error, setError] = useState("");

	const handleObjectiveChange = (objective: MetaAdsCampaignObjective) => {
		const baseReset: Partial<Parameters<typeof updateDraft>[0]> = {
			objective,
			leadFormId: "",
			leadForm: null,
			leadFormMetaId: null,
			pixelId: "",
			frequencyCapImpressions: undefined,
			frequencyCapIntervalDays: undefined,
			messagingGreeting: undefined,
			conversionEvent: undefined,
			phoneNumber: undefined,
			dynamicCreativeOptimization: undefined,
			attributionWindows: undefined,
		};

		switch (objective) {
			case MetaAdsCampaignObjective.OUTCOME_AWARENESS:
				updateDraft({
					...baseReset,
					optimizationGoal: MetaAdsOptimizationGoal.REACH,
					frequencyCapImpressions: 2,
					frequencyCapIntervalDays: 7,
				});
				break;
			case MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT:
				updateDraft({
					...baseReset,
					conversionLocation: MetaAdsConversionLocation.ON_POST,
					optimizationGoal: getDefaultMetaAdsOptimizationGoal(
						objective,
						MetaAdsConversionLocation.ON_POST,
					),
				});
				break;
			case MetaAdsCampaignObjective.OUTCOME_LEADS:
				updateDraft({
					...baseReset,
					conversionLocation: MetaAdsConversionLocation.INSTANT_FORM,
					optimizationGoal: getDefaultMetaAdsOptimizationGoal(
						objective,
						MetaAdsConversionLocation.INSTANT_FORM,
					),
				});
				break;
			case MetaAdsCampaignObjective.OUTCOME_SALES:
				updateDraft({
					...baseReset,
					conversionLocation: MetaAdsConversionLocation.WEBSITE,
					optimizationGoal: getDefaultMetaAdsOptimizationGoal(
						objective,
						MetaAdsConversionLocation.WEBSITE,
					),
					conversionEvent: MetaAdsConversionEvent.PURCHASE,
					attributionWindows: [
						MetaAdsAttributionWindow.SEVEN_DAY_CLICK,
						MetaAdsAttributionWindow.ONE_DAY_VIEW,
					],
				});
				break;
			default:
				updateDraft({
					...baseReset,
					optimizationGoal: getDefaultMetaAdsOptimizationGoal(objective),
				});
				break;
		}
	};

	const handleNext = () => {
		if (!draft.name.trim()) {
			setError("Campaign name is required.");
			return;
		}
		if (draft.campaignBudgetOptimization && draft.budgetAmount <= 0) {
			setError("Budget amount must be greater than 0.");
			return;
		}
		setError("");
		nextStep();
	};

	return (
		<Card>
			<Card.Header>Campaign Settings</Card.Header>
			<Card.Body>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					<div>
						<label className="form-label" htmlFor="campaign-name">
							Campaign Name
						</label>
						<input
							id="campaign-name"
							type="text"
							className="form-control"
							value={draft.name}
							onChange={(e) => updateDraft({ name: e.target.value })}
							placeholder="Enter campaign name"
						/>
					</div>

					<div>
						<label className="form-label" htmlFor="campaign-objective">
							Objective
						</label>
						<Select
							id="campaign-objective"
							className="form-select"
							value={draft.objective}
							onChange={(e) =>
								handleObjectiveChange(
									e.target.value as MetaAdsCampaignObjective,
								)
							}
						>
							<option value={MetaAdsCampaignObjective.OUTCOME_AWARENESS}>
								Awareness
							</option>
							<option value={MetaAdsCampaignObjective.OUTCOME_TRAFFIC}>
								Traffic
							</option>
							<option value={MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT}>
								Engagement
							</option>
							<option value={MetaAdsCampaignObjective.OUTCOME_LEADS}>
								Leads
							</option>
							<option value={MetaAdsCampaignObjective.OUTCOME_SALES}>
								Sales
							</option>
						</Select>
						<small className="text-muted">
							{OBJECTIVE_DESCRIPTIONS[draft.objective]}
						</small>
					</div>

					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							id="cbo-toggle"
							checked={draft.campaignBudgetOptimization}
							onChange={(e) =>
								updateDraft({
									campaignBudgetOptimization: e.target.checked,
								})
							}
						/>
						<label className="form-check-label" htmlFor="cbo-toggle">
							Campaign Budget Optimization (CBO)
						</label>
					</div>

					{draft.campaignBudgetOptimization && (
						<>
							<div>
								<label className="form-label" htmlFor="cbo-budget-type">
									Budget Type
								</label>
								<Select
									id="cbo-budget-type"
									className="form-select"
									value={draft.budgetType}
									onChange={(e) =>
										updateDraft({
											budgetType: e.target.value as MetaAdsBudgetType,
										})
									}
								>
									<option value={MetaAdsBudgetType.DAILY}>Daily Budget</option>
									<option value={MetaAdsBudgetType.LIFETIME}>
										Lifetime Budget
									</option>
								</Select>
							</div>
							<div>
								<label className="form-label" htmlFor="budget-amount">
									Budget Amount
								</label>
								<div className="input-group">
									<span className="input-group-text">$</span>
									<input
										id="budget-amount"
										type="number"
										className="form-control"
										value={centsToDollars(draft.budgetAmount)}
										onChange={(e) =>
											updateDraft({
												budgetAmount: dollarsToCents(Number(e.target.value)),
											})
										}
										min={0}
										step={0.01}
									/>
								</div>
							</div>
						</>
					)}

					{error && <div className="text-danger">{error}</div>}

					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleNext}
						>
							Next
						</button>
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
